import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { Router } from '@angular/router';

import { AuthenticationService } from '../services/authentication.service';
import { ConfigService } from '../services/config.service';
import { ROUTES } from '../constants/routes';

@Injectable()
export class DefaultHttpInterceptor implements HttpInterceptor {

  private static readonly JWT_KEY = 'jwt_token';

  private static readonly AUTH_LOGIN_URL = '/api/v1/auth/login';
  private static readonly AUTH_SET_PASSWORD_URL = '/api/v1/auth/set-password';
  private static readonly AUTH_IS_LOCKED_URL = '/api/v1/auth/is-locked';

  private static readonly SETTINGS_URL = '/settings/';
  private static readonly HEALTH_URL = '/actuator/health';

  constructor(
    private authenticationService: AuthenticationService,
    private configService: ConfigService,
    private router: Router
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    // OAuth2 refresh token
    if (
      req.url.includes('access_token')
      && this.configService.isAuthEnabled()
    ) {
      return this.handleRefreshToken(req, next);
    }

    // Routes publiques
    const isPublic =
      req.url.endsWith(DefaultHttpInterceptor.AUTH_LOGIN_URL)
      || req.url.endsWith(DefaultHttpInterceptor.AUTH_SET_PASSWORD_URL)
      || req.url.includes(DefaultHttpInterceptor.AUTH_IS_LOCKED_URL)
      || req.url.includes(DefaultHttpInterceptor.SETTINGS_URL)
      || req.url.includes(DefaultHttpInterceptor.HEALTH_URL);

    if (isPublic) {
      return next.handle(req);
    }

    // Authorization déjà présent
    if (req.headers.has('Authorization')) {
      return next.handle(req);
    }

    const token = this.resolveToken();

    if (!token) {
      console.warn('[HTTP] Aucun token disponible pour:', req.url);
      return next.handle(req);
    }

    const authRequest = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });

    return next.handle(authRequest).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401 || err.status === 403) {
          this.router.navigateByUrl(`/${ROUTES.UNAUTHORIZED_PATH}`);
        }
        return throwError(err);
      })
    );
  }

  private resolveToken(): string | null {

    // Mode FID
    if (this.configService.isAuthEnabled()) {

      if (this.authenticationService.hasValidIdToken()) {
        return this.authenticationService.getIdToken() ?? null;
      }

      return null;
    }

    // Mode interne
    return sessionStorage.getItem(DefaultHttpInterceptor.JWT_KEY);
  }

  private handleRefreshToken(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {

    let request = req.clone();

    try {

      const clientId = request.body.updates
        .find((u: any) => u.param === 'client_id')
        ?.value;

      const clientSecret = request.body.updates
        .find((u: any) => u.param === 'client_secret')
        ?.value;

      if (clientId && clientSecret) {

        request = req.clone({
          setHeaders: {
            Authorization: `Basic ${btoa(`${clientId}:${clientSecret}`)}`
          }
        });
      }

      request.body.updates = req.body.updates.filter(
        (u: any) => !['client_id', 'client_secret'].includes(u.param)
      );

    } catch (e) {
      console.warn('[HTTP] Erreur parsing refresh token body:', e);
    }

    return next.handle(request);
  }
}